import { Accordion, Center, Flex, Loader, Stack, Text, useMantineTheme } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { ExtractionDocumentStatus } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import type { PipelineStage } from '@dealstack/shared/lib/temporal/documentExtraction/types';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

type UIStageGroup =
	| 'validating'
	| 'classifying'
	| 'extracting_pass1'
	| 'extracting_pass2'
	| 'consolidating';

const UI_STAGE_CONFIG: Array<{
	key: UIStageGroup;
	labelKey: string;
	descriptionKey: string;
	pipelineStages: PipelineStage[];
	passNumber?: number;
}> = [
	{
		key: 'validating',
		labelKey: 'transactions.add_from_document.stage_validating',
		descriptionKey: 'transactions.add_from_document.stage_validating_desc',
		pipelineStages: ['ingestExtract'],
	},
	{
		key: 'classifying',
		labelKey: 'transactions.add_from_document.stage_classifying',
		descriptionKey: 'transactions.add_from_document.stage_classifying_desc',
		pipelineStages: ['separateChunks', 'consolidateChunks'],
	},
	{
		key: 'extracting_pass1',
		labelKey: 'transactions.add_from_document.stage_extracting',
		descriptionKey: 'transactions.add_from_document.stage_extracting_desc',
		pipelineStages: ['enrichChunks'],
		passNumber: 1,
	},
	{
		key: 'extracting_pass2',
		labelKey: 'transactions.add_from_document.stage_extracting',
		descriptionKey: 'transactions.add_from_document.stage_extracting_desc',
		pipelineStages: ['extractTransactions'],
		passNumber: 2,
	},
	{
		key: 'consolidating',
		labelKey: 'transactions.add_from_document.stage_consolidating',
		descriptionKey: 'transactions.add_from_document.stage_consolidating_desc',
		pipelineStages: ['consolidateTransactions'],
	},
];

function getUIStageStatus(
	documents: ExtractionDocumentStatus[],
	pipelineStages: PipelineStage[]
): 'pending' | 'running' | 'completed' {
	let hasRunning = false;
	let hasCompleted = false;
	let allCompleted = true;

	for (const doc of documents) {
		const stages = doc.pipelineStatus?.stages;
		if (!stages) {
			allCompleted = false;
			continue;
		}

		for (const stage of pipelineStages) {
			const stageStatus = stages[stage];
			if (!stageStatus || stageStatus.status === 'pending') {
				allCompleted = false;
			} else if (stageStatus.status === 'running') {
				hasRunning = true;
				allCompleted = false;
			} else if (stageStatus.status === 'completed') {
				hasCompleted = true;
			}
		}
	}

	if (hasRunning) {
		return 'running';
	}
	if (allCompleted && hasCompleted) {
		return 'completed';
	}
	if (hasCompleted) {
		return 'running';
	}
	return 'pending';
}

function getStageStatusLines(
	documents: ExtractionDocumentStatus[],
	pipelineStages: PipelineStage[],
	t: TranslateFunction<'cap-manage'>
): Array<{ documentId: string; text: string; status: 'completed' | 'running' | 'pending' }> {
	const lines: Array<{
		documentId: string;
		text: string;
		status: 'completed' | 'running' | 'pending';
	}> = [];

	for (const doc of documents) {
		const stages = doc.pipelineStatus?.stages;
		if (!stages) {
			lines.push({
				documentId: doc.documentId,
				text: `${doc.fileName} — ${t('transactions.add_from_document.status_pending')}`,
				status: 'pending',
			});
			continue;
		}

		let docGroupStatus: 'pending' | 'running' | 'completed' = 'pending';
		let progressText = '';

		for (const stage of pipelineStages) {
			const stageStatus = stages[stage];
			if (!stageStatus) {
				continue;
			}

			if (stageStatus.status === 'running') {
				docGroupStatus = 'running';
				if (stageStatus.progress && stageStatus.progress.total > 0) {
					const pct = Math.round(
						(stageStatus.progress.current / stageStatus.progress.total) * 100
					);
					progressText = ` (${pct}%)`;
				}
			} else if (stageStatus.status === 'completed') {
				docGroupStatus = 'completed';
			}
		}

		let statusText: string;
		if (docGroupStatus === 'completed') {
			statusText = t('transactions.add_from_document.status_processed');
		} else if (docGroupStatus === 'running') {
			statusText = `${t('transactions.add_from_document.status_processing')}${progressText}`;
		} else {
			statusText = t('transactions.add_from_document.status_pending');
		}
		lines.push({
			documentId: doc.documentId,
			text: `${doc.fileName} — ${statusText}`,
			status: docGroupStatus,
		});
	}

	return lines;
}

interface Props {
	documents: ExtractionDocumentStatus[];
	title?: string;
	minHeight?: number | string;
	maxWidth?: number | string;
}

export const ExtractionStagesAccordion = ({
	documents,
	title,
	minHeight = 400,
	maxWidth = 600,
}: Props) => {
	const theme = useMantineTheme();
	const tCapManage = useTranslationCapManage();

	const resolvedTitle =
		title === undefined
			? tCapManage('transactions.add_from_document.processing_uploads')
			: title;

	return (
		<Center style={{ minHeight }}>
			<Stack gap="md" style={{ width: '100%', maxWidth }}>
				{resolvedTitle && (
					<Text size="H3Thin" fw={500} ta="center">
						{resolvedTitle}
					</Text>
				)}

				<Accordion
					multiple
					defaultValue={UI_STAGE_CONFIG.map((s) => s.key)}
					variant="contained"
					styles={{
						item: {
							borderColor: theme.colors.gray[4],
							'&[data-active]': { backgroundColor: 'transparent' },
						},
						control: { padding: '0.75rem 1rem' },
						content: { padding: '0 1rem 0.75rem' },
						chevron: { color: theme.colors.gray[9] },
					}}
				>
					{UI_STAGE_CONFIG.map((stageConfig) => {
						const stageStatus = getUIStageStatus(documents, stageConfig.pipelineStages);
						const statusLines = getStageStatusLines(
							documents,
							stageConfig.pipelineStages,
							tCapManage
						);

						return (
							<Accordion.Item key={stageConfig.key} value={stageConfig.key}>
								<Accordion.Control>
									<div>
										<Flex align="center" gap="xs">
											<Text fw={600} size="sm">
												{tCapManage(stageConfig.labelKey)}
											</Text>
											{stageConfig.passNumber && (
												<Text
													size="xs"
													fw={500}
													color={theme.colors.gray[6]}
												>
													—{' '}
													{tCapManage(
														'transactions.add_from_document.pass_label',
														{ number: stageConfig.passNumber }
													)}
												</Text>
											)}
										</Flex>
										<Text size="xs" color={theme.colors.gray[9]}>
											{tCapManage(stageConfig.descriptionKey)}
										</Text>
									</div>
								</Accordion.Control>
								<Accordion.Panel>
									<Stack gap="xs">
										{statusLines.map((line) => (
											<Flex key={line.documentId} align="center" gap="xs">
												{line.status === 'completed' && (
													<Icon
														variant="tick-circle"
														size="md"
														color={theme.colors.green[6]}
													/>
												)}
												{line.status === 'running' && (
													<Loader
														size="xs"
														color={theme.colors.blue[6]}
													/>
												)}
												{line.status === 'pending' && (
													<Loader
														size="xs"
														color={theme.colors.gray[4]}
													/>
												)}
												<Text size="sm" color={theme.colors.gray[9]}>
													{line.text}
												</Text>
											</Flex>
										))}
										{statusLines.length === 0 && stageStatus === 'pending' && (
											<Text size="sm" color={theme.colors.gray[8]}>
												{tCapManage(
													'transactions.add_from_document.waiting'
												)}
											</Text>
										)}
									</Stack>
								</Accordion.Panel>
							</Accordion.Item>
						);
					})}
				</Accordion>
			</Stack>
		</Center>
	);
};
